<script setup lang="ts">
import Cookies from 'js-cookie'
import { getKeycloakLoginLink, getKeycloakRegisterLink, showWalletLink } from '../../utilities/useLinks'
import { getPendingInvestmentCookie } from '../../utilities/investment-process/usePendingInvestmentProcess'
import { onMounted, ref, computed } from 'vue'
import {
    isNaturalProfile,
    switchToLegalProfile,
    switchToNaturalProfile,
} from '../../utilities/investment-process/useUserProfileType'
import { useLegalAccountDataState } from '../../utilities/account/useAccountDataLegal'
import { usePersonalAccountDataState } from '../../utilities/account/useAccountDataPersonal'
import { createCompanyProfile } from '../../utilities/api'

const condaIsUserLoggedIn = computed(() => {
    return Cookies.get('conda_user_token') !== undefined
})

const profileType = computed(() => {
    return Cookies.get('conda_user_profile') ?? 'personal'
})

const showCreateCompanyProfileModal = ref(false)

//@ts-ignore
const accountStateNatural = usePersonalAccountDataState()
const accountStateLegal = useLegalAccountDataState()

onMounted(async () => {
    if (condaIsUserLoggedIn.value === true) {
        await accountStateNatural.fetchAccountData()
        await accountStateLegal.fetchAccountData()
    }
})

function hasPendingInvestmentNatural() {
    const cookieData = getPendingInvestmentCookie()
    if (
        cookieData !== null &&
        cookieData.account_type === 'natural' &&
        cookieData.natural_profile_uid === accountStateNatural.naturalProfile.natural_profile_uid
    ) {
        return true
    }
}

function hasPendingInvestmentLegal() {
    const cookieData = getPendingInvestmentCookie()
    if (
        cookieData !== null &&
        cookieData.account_type === 'legal' &&
        cookieData.natural_profile_uid === accountStateNatural.naturalProfile.natural_profile_uid
    ) {
        return true
    }
}

function condaIsExternalKycEnabled() {
    return window.externalKyc ?? false
}

function condaIsNewThemeEnabled() {
    return window.condaLayoutSettings.investment_desk_settings.use_new_theme
}

function userIsInInvestion() {
    return window.location.href.toString().includes('investieren')
}

function condaAccountHasAlert(): boolean {
    // check natural profile
    if (accountStateNatural.loaded && isNaturalProfile() && accountStateNatural.accountId) {
        if (
            !accountStateNatural.naturalProfile.date_of_birth ||
            !accountStateNatural.naturalProfile.address ||
            !accountStateNatural.naturalProfile.postal_code ||
            !accountStateNatural.naturalProfile.city ||
            !accountStateNatural.naturalProfile.country ||
            !accountStateNatural.naturalProfile.nationality ||
            accountStateNatural.naturalProfile.country.toString().trim() === '' ||
            !accountStateNatural.isVerified
        ) {
            return true
        }
    }
    // check legal profile
    if (
        accountStateLegal.loaded &&
        accountStateLegal.legalProfile.legal_profile_uid !== '' &&
        condaIsCompanyAccountVisible()
    ) {
        if (
            !accountStateLegal.legalProfile.company_name ||
            !accountStateLegal.legalProfile.registration_number ||
            !accountStateLegal.legalProfile.address ||
            !accountStateLegal.legalProfile.postal_code ||
            !accountStateLegal.legalProfile.city ||
            !accountStateLegal.legalProfile.country ||
            accountStateLegal.legalProfile.country.toString().trim() === '' ||
            !accountStateLegal.isVerified
        ) {
            return true
        }
    }
    // check for unfinished investment process
    if ((hasPendingInvestmentNatural() || hasPendingInvestmentLegal()) && !userIsInInvestion()) {
        return true
    }
}

function condaIsCompanyAccountVisible(): boolean {
    return (
        window.condaLayoutSettings.legal_profiles_active &&
        !window.condaLayoutSettings.investment_desk_settings.hide_company_account
    )
}

function hideCreateCompanyAccount(): boolean {
    return window.condaLayoutSettings.investment_desk_settings.hide_create_company_account
}

async function createCompanyProfileAndReloadAccountData() {
    await createCompanyProfile()
    await useLegalAccountDataState().fetchAccountData()
    window.location.reload()
}

const isCreateCompanyAccountVisible = computed(() => {
    return (
        condaIsCompanyAccountVisible() &&
        !accountStateNatural.hasLegalProfile &&
        accountStateNatural.naturalProfile.kyc_id
    )
})
</script>
<template>
    <!-- Not logged in -->
    <div v-if="!condaIsUserLoggedIn" class="d-flex register-login-buttons">
        <div v-if="!condaIsNewThemeEnabled()" class="d-flex align-items-center">
            <span class="text-gray-blue d-none-md mr-2">
                {{ $t('header_new_investor') }}
            </span>
        </div>
        <div :class="`d-flex align-items-center ${condaIsNewThemeEnabled() ? 'user-register-button' : ''}`">
            <a
                :href="getKeycloakRegisterLink()"
                :class="
                    condaIsNewThemeEnabled()
                        ? 'i-btn i-btn-secondary i-btn-sm rounded-sm bg-secondary'
                        : 'text-secondary'
                ">
                {{ $t('header_register') }}
            </a>
        </div>
        <div :class="`d-flex align-items-center ${condaIsNewThemeEnabled() ? 'user-login-button' : ''}`">
            <a
                :href="getKeycloakLoginLink()"
                :class="`i-btn i-btn-sm ${
                    condaIsNewThemeEnabled()
                        ? 'ml-2 mr-12 rounded-sm border border-white text-white hover:text-black xl:mr-0'
                        : 'i-btn-secondary my-1 ml-2 bg-secondary'
                }`">
                {{ $t('header_sign_in') }}
            </a>
        </div>
    </div>
    <!-- End Not logged in-->
    <!-- Account selection -->
    <div v-else class="d-flex align-items-center account dropdown-select ml-4">
        <div class="spec-dropdown dropdown-title text-gray d-flex align-items-center" style="position: relative">
            <span class="icon ion-person mr-2"></span>
            <span>
                <span class="d-none-md">
                    {{ accountStateNatural.naturalProfile.first_name }}
                    {{ accountStateNatural.naturalProfile.last_name }}
                </span>
                <i
                    v-if="condaAccountHasAlert()"
                    class="icon ion-android-alert alert-unfinished upper-alert-unfinished"></i>
            </span>
        </div>
        <ul
            class="user-dropdown dropdown-list ml-0 list-none bg-white"
            style="font-size: 120%; position: absolute; right: 0; top: 35px">
            <li class="text-gray-dark bold select-item d-flex align-items-center justify-between p-2">
                {{ $t('welcome') }}
            </li>
            <!-- start personal account item -->
            <li
                class="select-item d-flex align-items-center bg-hover-gray-lightest justify-between p-2"
                data-profile-type="personal">
                <a
                    href="#"
                    :class="`d-flex align-items-center bold account-switch account-type-personal justify-between text-${
                        profileType === 'personal' ? 'secondary' : 'gray'
                    }`"
                    @click="switchToNaturalProfile('account')">
                    <span class="icon ion-person mr-2"></span>
                    <p class="mb-0">{{ $t('personal_account') }}</p>
                </a>
                <a
                    v-if="showWalletLink"
                    href="#"
                    class="account-switch tooltip-wrapp ml-auto"
                    @click="switchToNaturalProfile('wallet')">
                    <i class="fa fa-credit-card text-gray-light text-hover-gray mx-2" aria-hidden="true"></i>
                    <span class="tooltip text-white">{{ $t('account_dropdown_tooltip_wallets') }}</span>
                </a>

                <a
                    href="#"
                    :class="`account-switch ${!showWalletLink ? 'ml-auto' : ''} tooltip-wrapp`"
                    @click="switchToNaturalProfile('orders')">
                    <span v-if="hasPendingInvestmentNatural()">
                        <span class="tooltip text-white">{{ $t('header_unfinished_investment_process') }}</span>
                        <i class="icon ion-bag text-gray-light text-hover-gray mx-2" aria-hidden="true"></i>
                        <i class="icon ion-android-alert alert-unfinished"></i>
                    </span>
                    <span v-else>
                        <span class="tooltip text-white">{{ $t('account_dropdown_tooltip_orders') }}</span>
                        <i class="icon ion-bag text-gray-light text-hover-gray mx-2" aria-hidden="true"></i>
                    </span>
                </a>
                <a href="#" class="account-switch tooltip-wrapp" @click="switchToNaturalProfile('account')">
                    <span v-if="!accountStateNatural.hasAllRequiredNaturalProfileAttributesForInvestment">
                        <span class="tooltip text-white">
                            {{ $t('account_dropdown_tooltip_fill_account_data') }}
                        </span>
                        <i class="icon ion-gear-b text-gray-light text-hover-gray mx-2" ariahidden="true"></i>
                        <i class="icon ion-android-alert alert-unfinished"></i>
                    </span>

                    <span v-if="accountStateNatural.hasAllRequiredNaturalProfileAttributesForInvestment">
                        <span class="tooltip text-white">
                            {{ $t('account_dropdown_tooltip_account') }}
                        </span>
                        <i class="icon ion-gear-b text-gray-light text-hover-gray mx-2" ariahidden="true"></i>
                    </span>
                </a>
            </li>
            <!-- end personal account item -->

            <!-- start legal account item -->
            <li
                v-if="
                    condaIsCompanyAccountVisible() &&
                    accountStateNatural.hasLegalProfile &&
                    accountStateLegal.legalProfile.legal_profile_uid !== ''
                "
                class="select-item d-flex align-items-center bg-hover-gray-lightest justify-between p-2"
                data-profile-type="company">
                <a
                    href="#"
                    :class="`d-flex align-items-center bold account-switch account-type-company justify-between text-${
                        profileType === 'company' ? 'secondary' : 'gray'
                    }`"
                    @click="switchToLegalProfile('account')">
                    <span class="icon ion-ios-briefcase mr-2"></span>
                    <p class="mb-0">{{ $t('account_dropdown_company_account') }}</p>
                </a>
                <a
                    v-if="showWalletLink"
                    href="#"
                    class="account-switch tooltip-wrapp ml-auto"
                    @click="switchToLegalProfile('wallet')">
                    <i class="fa fa-credit-card text-gray-light text-hover-gray mx-2" aria-hidden="true"></i>
                    <span class="tooltip text-white">{{ $t('account_dropdown_tooltip_wallets') }}</span>
                </a>

                <a href="#" class="account-switch tooltip-wrapp" @click="switchToLegalProfile('orders')">
                    <span v-if="hasPendingInvestmentLegal()">
                        <span class="tooltip text-white">{{ $t('header_unfinished_investment_process') }}</span>
                        <i class="icon ion-bag text-gray-light text-hover-gray mx-2" aria-hidden="true"></i>
                        <i class="icon ion-android-alert alert-unfinished"></i>
                    </span>
                    <span v-else>
                        <span class="tooltip text-white">{{ $t('account_dropdown_tooltip_orders') }}</span>
                        <i class="icon ion-bag text-gray-light text-hover-gray mx-2" aria-hidden="true"></i>
                    </span>
                </a>
                <a href="#" class="account-switch tooltip-wrapp" @click="switchToLegalProfile('account')">
                    <span v-if="!accountStateLegal.hasAllRequiredProfileAttributesForInvestment">
                        <span class="tooltip text-white">
                            {{ $t('account_dropdown_tooltip_fill_account_data') }}
                        </span>
                        <i class="icon ion-gear-b text-gray-light text-hover-gray mx-2" ariahidden="true"></i>
                        <i class="icon ion-android-alert alert-unfinished"></i>
                    </span>
                    <span v-else>
                        <span class="tooltip text-white">
                            {{ $t('account_dropdown_tooltip_account') }}
                        </span>
                        <i class="icon ion-gear-b text-gray-light text-hover-gray mx-2" ariahidden="true"></i>
                    </span>
                </a>
            </li>
            <!-- end legal account item -->

            <!-- start create company account item -->

            <li
                v-if="isCreateCompanyAccountVisible"
                class="text-gray select-item d-flex align-items-center bold justify-between p-2">
                <a
                    href="#"
                    class="text-gray w100 d-flex align-items-center bold create-company-profile justify-between"
                    @click="showCreateCompanyProfileModal = true">
                    <div class="d-flex align-items-center">
                        <span class="icon ion-ios-briefcase mr-2"></span>
                        <p class="mb-0">{{ $t('account_dropdown_company_account') }}</p>
                    </div>
                    <i
                        class="icon ion-plus-circled text-secondary-light text-hover-secondary mx-2"
                        aria-hidden="true"></i
                ></a>
            </li>
            <!-- end create company account item -->
            <!-- start logout item -->
            <li class="text-gray select-item d-flex align-items-center bold justify-between p-2">
                <a
                    :href="'/?conda_kc_logout=true'"
                    class="w100 text-gray-blue d-flex align-items-center bold justify-start">
                    <span class="icon ion-log-out mr-2"></span>
                    <p class="mb-0">{{ $t('account_dropdown_log_out') }}</p>
                </a>
            </li>
            <!-- end logout item -->
        </ul>
        <div
            v-if="showCreateCompanyProfileModal"
            class="fixed left-0 top-0 z-50 flex h-screen w-full items-center justify-center bg-black/50"
            @click="showCreateCompanyProfileModal = false">
            <div class="h-auto w-1/3 rounded-md bg-white px-8" @click.stop>
                <div class="flex h-auto w-full items-center justify-center py-6">
                    <div class="flex h-12 w-12 items-center justify-center rounded-lg bg-green-200/60 p-2">
                        <span class="icon ion-ios-briefcase text-secondary-500"></span>
                    </div>
                </div>
                <div class="flex h-auto w-full items-center justify-center py-4">
                    <h2 class="max-w-sm text-center text-primary-500">{{ $t('create_company_profile_question') }}</h2>
                </div>
                <div class="flex h-auto w-full flex-col items-center justify-center space-y-2 py-4">
                    <button
                        class="h-12 w-full rounded-lg bg-secondary-500 text-base text-white duration-150 hover:scale-[102%] hover:bg-[#3a9176]"
                        @click="createCompanyProfileAndReloadAccountData()">
                        {{ $t('create_company_profile') }}
                    </button>
                    <button
                        class="h-12 w-full rounded-lg bg-white text-base text-primary-500 duration-150 hover:scale-[102%] hover:bg-[#e6f0ee]"
                        @click="showCreateCompanyProfileModal = false">
                        {{ $t('discard') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.register-login-buttons-mobile {
    display: none !important;
}

@media screen and (max-width: 680px) {
    #main_menu_v2 .container {
        flex-direction: column;
        padding-bottom: 1.5rem;
    }

    #main_menu_v2 .main-nav {
        height: 0;
    }

    #main_menu_v2 .register-login {
        margin: 0;
    }

    #main_menu_v2 .register-login-buttons {
        justify-content: center;
        gap: 0 0.5rem;
    }

    #main_menu_v2 .user-login-button a {
        margin: 0;
    }

    #main_menu_v2 #toggle:checked ~ .nav {
        margin-top: 3.5rem !important;
    }

    .custom_conda_capital_header {
        padding-top: 12rem !important;
    }

    .user-dropdown {
        top: 12rem !important;
        width: calc(100vw - 2rem) !important;
        left: 1rem !important;
        right: 1rem !important;
    }
}
</style>
